<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied />
  </div>
  <div v-else-if="featureUnavailable" class="m-3">
    <feature-not-available back-route="reports" back-text="_plan.Back to reports" />
  </div>
  <div v-else class="p-3">
    <!-- Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
      <div class="d-flex align-items-center gap-2">
        <div>
          <h5 class="fw-bold mb-0">
            <i class="fa-solid fa-arrows-rotate me-2" style="color: #0ea5e9;"></i>{{ $t('Stock Movement') }}
          </h5>
          <small class="text-muted">{{ $t('Inventory & Stock') }}</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-primary btn-sm" @click="downloadPdf" :disabled="pdfLoading || loading">
          <i class="fa-solid fa-download me-1"></i>{{ $t('Download') }}
        </button>
        <button class="btn btn-primary btn-sm" @click="previewPdf" :disabled="pdfLoading || loading">
          <span v-if="pdfLoading"><i class="fa-solid fa-spinner fa-spin me-1"></i>{{ $t('Generating') }}...</span>
          <span v-else><i class="fa-solid fa-file-pdf me-1"></i>{{ $t('Preview PDF') }}</span>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body py-2 px-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select v-model="filters.branch" class="form-select form-select-sm" style="min-width: 140px; max-width: 200px;" :disabled="loading">
            <option value="all">{{ $t('All Branches') }}</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
          <div class="input-group input-group-sm w-auto date-range-input-group">
            <span class="input-group-text"><i class="fa-regular fa-calendar"></i></span>
            <date-range-picker
              v-model="filters.date_range"
              :date-range="filters.date_range"
              :locale-data="dateRangeLocale"
              :ranges="dateRanges"
              :opens="'right'"
              :auto-apply="true"
              :show-dropdowns="true"
              :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate && picker.endDate">
                  {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
                </span>
                <span v-else class="text-muted">{{ $t('Select date range') }}</span>
              </template>
            </date-range-picker>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="loadReport">{{ $t('Retry') }}</button>
    </div>

    <div :class="{ 'report-loading': loading }">

    <!-- Stock Movement Table -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-0 py-3">
        <h6 class="mb-0 fw-semibold"><i class="fa-solid fa-arrows-rotate me-2 text-info"></i>{{ $t('Stock Movement') }}</h6>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive table-scroll">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3">{{ $t('Item') }}</th>
                <th class="text-end">{{ $t('Opening') }}</th>
                <th class="text-end">{{ $t('Received') }}</th>
                <th class="text-end">{{ $t('Used') }}</th>
                <th class="text-end">{{ $t('Produced') }}</th>
                <th class="text-end">{{ $t('Used in Production') }}</th>
                <th class="text-end">{{ $t('Waste') }}</th>
                <th class="text-end">{{ $t('Staff Meal') }}</th>
                <th class="text-end">{{ $t('Buffet Usage') }}</th>
                <th class="text-end">{{ $t('Adjustment') }}</th>
                <th class="text-end pe-3">{{ $t('Closing') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!stockMovementData.length && !loading">
                <td colspan="11" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr v-for="s in stockMovementData" :key="s.inventory_id">
                <td class="ps-3 fw-medium">{{ s.item }}</td>
                <td class="text-end">{{ s.opening }} {{ $t(s.unit) }}</td>
                <td class="text-end text-success">+{{ s.received }} {{ $t(s.unit) }}</td>
                <td class="text-end text-primary">-{{ s.used }} {{ $t(s.unit) }}</td>
                <!-- ผลิต: ผลผลิตเข้า / วัตถุดิบออกไปผลิต (สุทธิหลัง Reverse Record) — ไม่มี 2 ช่องนี้ยอดกระทบกันไม่ได้ -->
                <td class="text-end" :class="s.production_in ? 'text-success' : 'text-muted'">
                  {{ s.production_in ? '+' : '' }}{{ s.production_in || 0 }} {{ $t(s.unit) }}
                </td>
                <td class="text-end" :class="s.production_out ? 'text-primary' : 'text-muted'">
                  {{ s.production_out ? '-' : '' }}{{ s.production_out || 0 }} {{ $t(s.unit) }}
                </td>
                <td class="text-end text-danger">-{{ s.waste }} {{ $t(s.unit) }}</td>
                <td class="text-end" :class="s.staff_meal > 0 ? 'text-warning' : 'text-muted'">
                  {{ s.staff_meal > 0 ? '-' : '' }}{{ s.staff_meal || 0 }} {{ $t(s.unit) }}
                </td>
                <td class="text-end" :class="s.buffet_usage > 0 ? 'text-info' : 'text-muted'">
                  {{ s.buffet_usage > 0 ? '-' : '' }}{{ s.buffet_usage || 0 }} {{ $t(s.unit) }}
                </td>
                <td class="text-end" :class="adjustmentClass(s.adjustment)">
                  {{ formatSigned(s.adjustment) }} {{ $t(s.unit) }}
                </td>
                <td class="text-end pe-3 fw-semibold">{{ s.closing }} {{ $t(s.unit) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    </div><!-- /loading wrapper -->

    <div v-if="loading" class="report-loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('Loading') + '...' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { Reports } from '@/services/endpoint/reports.service';
import reportMixin from './reportMixin';

export default {
  name: 'ReportStockMovement',
  mixins: [reportMixin],
  data() {
    return {
      reportLabel: 'Stock Movement',
      items: [],
    };
  },
  methods: {
    async fetchReport() {
      const res = await Reports.stockMovement(this.buildFetchParams());
      const payload = res?.data?.data || {};
      const filter  = res?.data?.filter || {};
      this.branches = filter.branches || [];
      this.items    = payload.items || [];
    },

    async buildPdfDefinition() {
      const content = [
        ...this.buildPdfHeader(),
        { text: this.$t('Stock Movement'), style: 'sectionTitle' },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: this.$t('Item'), style: 'tableHeader' },
                { text: this.$t('Opening'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Received'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Used'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Produced'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Used in Production'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Waste'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Staff Meal'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Buffet Usage'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Adjustment'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Closing'), style: 'tableHeader', alignment: 'right' },
              ],
              ...this.stockMovementData.map(s => [
                { text: s.item, style: 'tableCell' },
                { text: `${s.opening} ${s.unit}`, style: 'tableCell', alignment: 'right' },
                { text: `+${s.received} ${s.unit}`, style: 'tableCell', alignment: 'right', color: '#22c55e' },
                { text: `-${s.used} ${s.unit}`, style: 'tableCell', alignment: 'right', color: '#3b82f6' },
                { text: `${s.production_in ? '+' : ''}${s.production_in || 0} ${s.unit}`, style: 'tableCell', alignment: 'right', color: s.production_in ? '#22c55e' : undefined },
                { text: `${s.production_out ? '-' : ''}${s.production_out || 0} ${s.unit}`, style: 'tableCell', alignment: 'right', color: s.production_out ? '#3b82f6' : undefined },
                { text: `-${s.waste} ${s.unit}`, style: 'tableCell', alignment: 'right', color: '#ef4444' },
                { text: `${(s.staff_meal || 0) > 0 ? '-' : ''}${s.staff_meal || 0} ${s.unit}`, style: 'tableCell', alignment: 'right', color: (s.staff_meal || 0) > 0 ? '#f59e0b' : undefined },
                { text: `${(s.buffet_usage || 0) > 0 ? '-' : ''}${s.buffet_usage || 0} ${s.unit}`, style: 'tableCell', alignment: 'right', color: (s.buffet_usage || 0) > 0 ? '#06b6d4' : undefined },
                { text: `${this.formatSigned(s.adjustment)} ${s.unit}`, style: 'tableCell', alignment: 'right', color: this.adjustmentColor(s.adjustment) },
                { text: `${s.closing} ${s.unit}`, style: 'tableCell', alignment: 'right' },
              ]),
            ],
          },
          layout: 'lightHorizontalLines',
        },
      ];
      return { content };
    },

    formatSigned(v) {
      const n = Number(v || 0);
      if (n > 0) return `+${n}`;
      return String(n);
    },
    adjustmentClass(v) {
      const n = Number(v || 0);
      if (n > 0) return 'text-success';
      if (n < 0) return 'text-danger';
      return 'text-muted';
    },
    adjustmentColor(v) {
      const n = Number(v || 0);
      if (n > 0) return '#22c55e';
      if (n < 0) return '#ef4444';
      return undefined;
    },
  },
  computed: {
    stockMovementData() {
      return this.items || [];
    },
  },
};
</script>

<style scoped>
.report-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.report-loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
}

/* Sticky thead when the movement table grows tall */
.table-scroll {
  max-height: 60vh;
  overflow-y: auto;
}
.table-scroll thead.table-light th {
  position: sticky;
  top: 0;
  background-color: #f8f9fa;
  z-index: 2;
}
</style>
